import { z } from 'zod';
import { CursorPaginationSchema } from './shared/pagination.js';

// ============================================================================
// Admin Inngest - Common types
// ============================================================================

export const InngestFunctionStatsSchema = z.object({
  runs24h: z.number(),
  successRate: z.number(),
  avgDurationMs: z.number(),
  p95DurationMs: z.number(),
});

export const InngestFunctionSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  triggers: z.array(z.unknown()),
  concurrency: z.union([z.string(), z.number()]),
  throttle: z.union([z.string(), z.number()]),
  stats: InngestFunctionStatsSchema.nullable(),
});

export const InngestFunctionTemplateSchema = z.object({
  functionId: z.string(),
  functionName: z.string(),
  slug: z.string(),
  triggerType: z.enum(['event', 'cron']),
  eventName: z.string().nullable(),
  description: z.string(),
  defaultPayload: z.record(z.string(), z.unknown()),
  caution: z.string().nullable().optional(),
  docsUrl: z.string().url().nullable().optional(),
  tags: z.array(z.string()).optional(),
});

export type InngestFunctionStats = z.infer<typeof InngestFunctionStatsSchema>;
export type InngestFunction = z.infer<typeof InngestFunctionSchema>;
export type InngestFunctionTemplate = z.infer<typeof InngestFunctionTemplateSchema>;

// ============================================================================
// Admin Inngest - List functions
// ============================================================================

export const AdminInngestFunctionsResponseSchema = z.object({
  data: z.array(InngestFunctionSchema),
  templates: z.array(InngestFunctionTemplateSchema),
});

export type AdminInngestFunctionsResponse = z.infer<typeof AdminInngestFunctionsResponseSchema>;

// ============================================================================
// Admin Inngest - Trigger run
// ============================================================================

export const AdminInngestTriggerRunBodySchema = z.object({
  functionId: z.string(),
  eventName: z.string().optional(),
  payload: z.record(z.string(), z.unknown()).optional(),
});

export const AdminInngestTriggerRunResponseSchema = z.object({
  ok: z.boolean(),
  functionId: z.string(),
  eventName: z.string(),
  eventIds: z.array(z.string()),
  payload: z.record(z.string(), z.unknown()),
  triggeredAt: z.date(),
});

export type AdminInngestTriggerRunBody = z.infer<typeof AdminInngestTriggerRunBodySchema>;
export type AdminInngestTriggerRunResponse = z.infer<typeof AdminInngestTriggerRunResponseSchema>;

// ============================================================================
// Admin Inngest - Get event runs
// ============================================================================

export const AdminInngestEventRunsParamsSchema = z.object({
  eventId: z.string(),
});

export const InngestRunStatusSchema = z.enum(['Queued', 'Running', 'Completed', 'Failed', 'Cancelled']);

const InngestRunErrorSchema = z.object({
  message: z.string(),
  stack: z.string().optional(),
  name: z.string().optional(),
  code: z.string().optional(),
});

const InngestRunUserSchema = z
  .object({
    id: z.string().optional(),
    email: z.string().email().optional(),
    name: z.string().optional(),
  })
  .partial();

export const InngestRunSchema = z.object({
  id: z.string(),
  eventId: z.string(),
  functionId: z.string(),
  functionName: z.string().optional(),
  functionVersion: z.number().int(),
  status: InngestRunStatusSchema,
  createdAt: z.date(),
  startedAt: z.date().nullable(),
  endedAt: z.date().nullable(),
  metadata: z.unknown().optional(),
  output: z.record(z.string(), z.unknown()).optional(),
  error: InngestRunErrorSchema.optional(),
  lastError: z.unknown().optional(),
  priority: z.number().int().optional(),
  user: InngestRunUserSchema.optional(),
  requestedBy: z.unknown().optional(),
  source: z.unknown().optional(),
  raw: z.unknown().optional(),
});

export const AdminInngestEventRunsResponseSchema = z.object({
  event: z.unknown(),
  runs: z.array(InngestRunSchema),
});

export type AdminInngestEventRunsParams = z.infer<typeof AdminInngestEventRunsParamsSchema>;
export type InngestRun = z.infer<typeof InngestRunSchema>;
export type InngestRunStatus = z.infer<typeof InngestRunStatusSchema>;
export type InngestRunError = z.infer<typeof InngestRunErrorSchema>;
export type InngestRunUser = z.infer<typeof InngestRunUserSchema>;
export type AdminInngestEventRunsResponse = z.infer<typeof AdminInngestEventRunsResponseSchema>;

// ============================================================================
// Admin Inngest - Queue overview
// ============================================================================

export const InngestQueueFunctionSchema = z.object({
  functionId: z.string(),
  functionName: z.string(),
  queued: z.number(),
  running: z.number(),
  completed: z.number(),
  failed: z.number(),
  concurrency: z.union([z.string(), z.number()]),
  throttle: z.union([z.string(), z.number()]),
  id: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  stats: InngestFunctionStatsSchema.nullable(),
});

export const InngestQueueSummarySchema = z.object({
  totalQueued: z.number(),
  totalRunning: z.number(),
  totalCompleted: z.number(),
  totalFailed: z.number(),
});

export const AdminInngestOverviewResponseSchema = z.object({
  summary: InngestQueueSummarySchema,
  functions: z.array(InngestQueueFunctionSchema),
});

export type InngestQueueFunction = z.infer<typeof InngestQueueFunctionSchema>;
export type InngestQueueSummary = z.infer<typeof InngestQueueSummarySchema>;
export type AdminInngestOverviewResponse = z.infer<typeof AdminInngestOverviewResponseSchema>;

// ============================================================================
// Admin Inngest - List runs with filters
// ============================================================================

export const AdminInngestRunsQuerySchema = z.object({
  limit: z.number().min(1).max(200).optional(),
  status: InngestRunStatusSchema.optional(),
  jobId: z.string().optional(),
  cursor: z.string().optional(),
});

export const AdminInngestRunsResponseSchema = z.object({
  data: z.array(InngestRunSchema),
  pagination: CursorPaginationSchema,
});

export type AdminInngestRunsQuery = z.infer<typeof AdminInngestRunsQuerySchema>;
export type AdminInngestRunsResponse = z.infer<typeof AdminInngestRunsResponseSchema>;

// ============================================================================
// Admin Inngest - Get/Cancel specific run
// ============================================================================

export const AdminInngestRunParamsSchema = z.object({
  runId: z.string(),
});

export const AdminInngestRunResponseSchema = z.unknown();

export const AdminInngestCancelRunResponseSchema = z.unknown();

export type AdminInngestRunParams = z.infer<typeof AdminInngestRunParamsSchema>;
export type AdminInngestRunResponse = z.infer<typeof AdminInngestRunResponseSchema>;
export type AdminInngestCancelRunResponse = z.infer<typeof AdminInngestCancelRunResponseSchema>;
